import * as React from 'react';
import type {ReactNode} from 'react';
import {MOBILE_OPTION_HEIGHT, MOBILE_SCROLL_SETTLE_DELAY} from './constants';
import {type SelectValue} from './types';
import {
  getFirstEnabledOptionIndex,
  getLastEnabledOptionIndex,
  getNextEnabledOptionIndex,
  getOptionIndex,
  getOptionScrollTop,
} from './select.utils';
import {type SelectOption} from '../../lib/selector-option';

type UseMobileSelectWheelParams<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
> = {
  isMobile: boolean;
  isOpen: boolean;
  options: SelectOption<T, V, L>[];
  value?: SelectOption<T, V, L> | null;
  disabled?: boolean;
};

function useMobileSelectWheel<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
>({isMobile, isOpen, options, value, disabled}: UseMobileSelectWheelParams<T, V, L>) {
  const [pendingValue, setPendingValue] = React.useState<SelectOption<T, V, L> | null>(
    value ?? null,
  );
  const [mobileScrollTop, setMobileScrollTop] = React.useState(0);
  const mobileListRef = React.useRef<HTMLDivElement>(null);
  const scrollSettleTimeoutRef = React.useRef<number | null>(null);
  const scrollAnimationFrameRef = React.useRef<number | null>(null);

  const getTargetIndex = React.useCallback(
    (targetValue?: SelectOption<T, V, L> | null) => {
      const selectedIndex = getOptionIndex(options, targetValue);

      return selectedIndex >= 0 ? selectedIndex : getFirstEnabledOptionIndex(options);
    },
    [options],
  );

  const syncScrollPosition = React.useCallback(
    (
      targetValue?: SelectOption<T, V, L> | null,
      behavior: ScrollBehavior = 'instant',
    ) => {
      const targetIndex = getTargetIndex(targetValue);
      if (targetIndex < 0) return;

      const nextScrollTop = getOptionScrollTop(targetIndex);
      setMobileScrollTop(nextScrollTop);

      if (mobileListRef.current) {
        mobileListRef.current.scrollTo({
          top: nextScrollTop,
          behavior,
        });
      }

      const targetOption = options[targetIndex];
      if (targetOption && !targetOption.isDisabled) {
        setPendingValue(targetOption);
      }
    },
    [getTargetIndex, options],
  );

  const clearScrollSettleTimeout = React.useCallback(() => {
    if (scrollSettleTimeoutRef.current === null) return;

    window.clearTimeout(scrollSettleTimeoutRef.current);
    scrollSettleTimeoutRef.current = null;
  }, []);

  const clearScrollAnimationFrame = React.useCallback(() => {
    if (scrollAnimationFrameRef.current === null) return;

    window.cancelAnimationFrame(scrollAnimationFrameRef.current);
    scrollAnimationFrameRef.current = null;
  }, []);

  React.useEffect(
    () => () => {
      clearScrollSettleTimeout();
      clearScrollAnimationFrame();
    },
    [clearScrollAnimationFrame, clearScrollSettleTimeout],
  );

  React.useEffect(() => {
    setPendingValue(value ?? null);
  }, [value]);

  React.useLayoutEffect(() => {
    if (!isMobile || !isOpen) return;

    const frameId = window.requestAnimationFrame(() => {
      syncScrollPosition(value ?? null, 'instant');
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isMobile, isOpen, syncScrollPosition, value]);

  const settleScroll = React.useCallback(() => {
    if (!mobileListRef.current) return;

    const nextIndex = Math.round(mobileListRef.current.scrollTop / MOBILE_OPTION_HEIGHT);
    const nextOption = options[nextIndex];
    const nextScrollTop = getOptionScrollTop(nextIndex);

    mobileListRef.current.scrollTop = nextScrollTop;
    setMobileScrollTop(nextScrollTop);

    if (
      !nextOption ||
      nextOption.isDisabled ||
      nextOption.value === pendingValue?.value
    ) {
      return;
    }

    setPendingValue(nextOption);
  }, [options, pendingValue]);

  const scheduleScrollSettle = React.useCallback(() => {
    clearScrollSettleTimeout();
    scrollSettleTimeoutRef.current = window.setTimeout(() => {
      settleScroll();
    }, MOBILE_SCROLL_SETTLE_DELAY);
  }, [clearScrollSettleTimeout, settleScroll]);

  const handleScroll = React.useCallback(() => {
    if (!mobileListRef.current) return;

    const nextScrollTop = mobileListRef.current.scrollTop;
    clearScrollAnimationFrame();

    scrollAnimationFrameRef.current = window.requestAnimationFrame(() => {
      setMobileScrollTop(nextScrollTop);
      scrollAnimationFrameRef.current = null;
    });

    scheduleScrollSettle();
  }, [clearScrollAnimationFrame, scheduleScrollSettle]);

  const focusOptionByIndex = React.useCallback(
    (
      index: number,
      behavior: ScrollBehavior = 'instant',
      updatePendingImmediately = behavior === 'instant',
    ) => {
      if (!mobileListRef.current || index < 0 || index >= options.length) return;

      const option = options[index];
      if (!option || option.isDisabled) return;

      const nextScrollTop = getOptionScrollTop(index);
      mobileListRef.current.scrollTo({top: nextScrollTop, behavior});

      if (updatePendingImmediately) {
        setPendingValue(option);
      }

      if (behavior === 'instant') {
        setMobileScrollTop(nextScrollTop);
        return;
      }

      scheduleScrollSettle();
    },
    [options, scheduleScrollSettle],
  );

  const handleOptionClick = React.useCallback(
    (option: SelectOption<T, V, L>) => {
      if (!mobileListRef.current || disabled || option.isDisabled) return;

      const optionIndex = getOptionIndex(options, option);
      if (optionIndex < 0) return;

      focusOptionByIndex(optionIndex, 'smooth', true);
    },
    [disabled, focusOptionByIndex, options],
  );

  const moveByStep = React.useCallback(
    (step: 1 | -1) => {
      const currentIndex = getOptionIndex(options, pendingValue);
      const fallbackIndex =
        step === 1
          ? getFirstEnabledOptionIndex(options)
          : getLastEnabledOptionIndex(options);
      const startIndex = currentIndex < 0 ? fallbackIndex : currentIndex + step;
      const nextIndex = getNextEnabledOptionIndex(options, startIndex, step);

      if (nextIndex >= 0) {
        focusOptionByIndex(nextIndex, 'smooth', true);
      }
    },
    [focusOptionByIndex, options, pendingValue],
  );

  const moveToBoundary = React.useCallback(
    (boundary: 'start' | 'end') => {
      const targetIndex =
        boundary === 'start'
          ? getFirstEnabledOptionIndex(options)
          : getLastEnabledOptionIndex(options);

      if (targetIndex >= 0) {
        focusOptionByIndex(targetIndex, 'smooth', true);
      }
    },
    [focusOptionByIndex, options],
  );

  const syncPendingValue = React.useCallback(
    (nextValue?: SelectOption<T, V, L> | null) => {
      const normalizedValue = nextValue ?? null;
      const matchedIndex = getOptionIndex(options, normalizedValue);
      const nextIndex =
        matchedIndex >= 0 ? matchedIndex : getFirstEnabledOptionIndex(options);

      setPendingValue(normalizedValue);

      if (nextIndex < 0) return;

      const nextScrollTop = getOptionScrollTop(nextIndex);
      setMobileScrollTop(nextScrollTop);

      if (mobileListRef.current) {
        mobileListRef.current.scrollTop = nextScrollTop;
      }
    },
    [options],
  );

  return {
    mobileListRef,
    mobileScrollTop,
    pendingValue,
    syncPendingValue,
    handleOptionClick,
    handleScroll,
    moveByStep,
    moveToBoundary,
  };
}

export {useMobileSelectWheel};
